import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Save, Trash2, Rocket, Compass, Search, X } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";

/**
 * AdminJourneyTypesManager — CRUD for the Journey business types shown on
 * the landing page selector. Each business type bundles a curated set of
 * program templates that visitors can preview and toggle before signing up.
 *
 * Admins can:
 *  - Create / rename / delete business types
 *  - Reorder + toggle active/inactive
 *  - Pick which templates apply (checkbox picker with search + category filter)
 *  - Edit EN + AR name, tagline, icon and URL slug in one panel
 */
const ICON_OPTIONS = ["store", "shopping-bag", "landmark", "briefcase", "factory", "trending-up", "sparkles"];

export function AdminJourneyTypesManager() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newSlug, setNewSlug] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/business-types");
      setItems(res.data?.items || []);
      setTemplates(res.data?.available_templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load journey types");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e?.preventDefault?.();
    const slug = newSlug.trim().toLowerCase();
    const name = newName.trim();
    if (!slug || !name) return;
    setCreating(true);
    try {
      const res = await client.post("/admin/business-types", { slug, name_en: name, order: items.length + 1 });
      setNewSlug(""); setNewName("");
      toast.success("Journey type created");
      setEditing(res.data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const patch = async (type_id, updates) => {
    setSaving(type_id);
    try {
      await client.patch(`/admin/business-types/${type_id}`, updates);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  const remove = async (type_id) => {
    if (!window.confirm("Delete this journey type? Visitors who picked it will no longer see it.")) return;
    try {
      await client.delete(`/admin/business-types/${type_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-panel-journey-types">
      <Card className="rounded-2xl border border-border p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Compass className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-xl font-semibold">Journey types</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage the business-type tabs shown on the Start-your-journey selector. Each type bundles a curated set of Program templates for its visitors.
            </p>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border border-border p-4">
        <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Display name (EN) — e.g. Franchise Owner" className="rounded-xl" data-testid="admin-journey-new-name" />
          <Input value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder="url-slug — e.g. franchise-owner" className="rounded-xl" data-testid="admin-journey-new-slug" />
          <Button type="submit" className="btn-brand rounded-xl gap-2" disabled={creating || !newSlug.trim() || !newName.trim()} data-testid="admin-journey-create">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add type
          </Button>
        </form>
      </Card>

      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="admin-journey-empty">
          <Compass className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No journey types yet — add one above.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((bt) => (
            <Card key={bt.type_id} className="rounded-2xl border border-border p-5 space-y-3" data-testid={`admin-journey-card-${bt.slug}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-display font-semibold truncate">{bt.name_en}</p>
                  {bt.name_ar && (
                    <p dir="rtl" className="text-xs text-muted-foreground truncate">{bt.name_ar}</p>
                  )}
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 font-mono">{bt.slug}</p>
                </div>
                <Switch
                  checked={bt.active}
                  onCheckedChange={(v) => patch(bt.type_id, { active: v })}
                  aria-label="Toggle active"
                  data-testid={`admin-journey-active-${bt.slug}`}
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <Badge variant="secondary" className="rounded-full">{bt.templates.length} programs</Badge>
                <Badge variant="outline" className="rounded-full">order · {bt.order}</Badge>
                <Badge variant="outline" className="rounded-full">icon · {bt.icon}</Badge>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" variant="outline" className="rounded-lg gap-1 flex-1" onClick={() => setEditing(bt)} disabled={saving === bt.type_id} data-testid={`admin-journey-edit-${bt.slug}`}>
                  {saving === bt.type_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
                  Edit & pick programs
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(bt.type_id)} aria-label="Delete" data-testid={`admin-journey-delete-${bt.slug}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <JourneyTypeEditDialog
        entry={editing}
        templates={templates}
        iconOptions={ICON_OPTIONS}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// JourneyTypeEditDialog — edit metadata + toggle which templates apply.
// ----------------------------------------------------------------------------
function JourneyTypeEditDialog({ entry, templates, iconOptions, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("_all");

  useEffect(() => {
    if (!entry) { setForm(null); return; }
    setForm({
      type_id: entry.type_id,
      slug: entry.slug,
      name_en: entry.name_en || "",
      name_ar: entry.name_ar || "",
      tagline_en: entry.tagline_en || "",
      tagline_ar: entry.tagline_ar || "",
      icon: entry.icon || "sparkles",
      order: entry.order || 0,
      active: entry.active !== false,
      template_ids: new Set(entry.template_ids || []),
    });
    setQ(""); setCat("_all");
  }, [entry]);

  const categories = useMemo(() => {
    const set = new Set(templates.map((t) => t.category).filter(Boolean));
    return ["_all", ...Array.from(set)];
  }, [templates]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return templates.filter((t) => {
      if (cat !== "_all" && t.category !== cat) return false;
      if (!term) return true;
      return (t.name || "").toLowerCase().includes(term) || (t.focus || "").toLowerCase().includes(term);
    });
  }, [templates, q, cat]);

  const toggle = (tid) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = new Set(prev.template_ids);
      next.has(tid) ? next.delete(tid) : next.add(tid);
      return { ...prev, template_ids: next };
    });
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await client.patch(`/admin/business-types/${form.type_id}`, {
        slug: form.slug,
        name_en: form.name_en,
        name_ar: form.name_ar,
        tagline_en: form.tagline_en,
        tagline_ar: form.tagline_ar,
        icon: form.icon,
        order: form.order,
        active: form.active,
        template_ids: Array.from(form.template_ids),
      });
      toast.success("Journey type saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!entry || !form) return null;

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="journey-type-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit journey type</DialogTitle>
          <DialogDescription>
            Choose which Program templates visitors see when they pick this business type. Names and taglines drive the landing tabs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Name (EN)</Label>
              <Input dir="ltr" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="rounded-xl mt-1" data-testid="journey-type-name-en" />
            </div>
            <div>
              <Label className="text-xs">Name (AR)</Label>
              <Input dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} className="rounded-xl mt-1" data-testid="journey-type-name-ar" />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Tagline (EN)</Label>
              <Input dir="ltr" value={form.tagline_en} onChange={(e) => setForm({ ...form, tagline_en: e.target.value })} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-xs">Tagline (AR)</Label>
              <Input dir="rtl" value={form.tagline_ar} onChange={(e) => setForm({ ...form, tagline_ar: e.target.value })} className="rounded-xl mt-1" />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs">Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} className="rounded-xl mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs">Order</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-xs">Icon</Label>
              <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="mt-1 w-full rounded-xl border border-input bg-transparent p-2 text-sm">
                {iconOptions.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <p className="font-display text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Programs · {form.template_ids.size} selected
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search programs" className="rounded-xl ps-8 h-8 w-48" data-testid="journey-type-templates-search" />
                </div>
                <select value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-xl border border-input bg-transparent p-1.5 text-xs" data-testid="journey-type-templates-category">
                  {categories.map((c) => <option key={c} value={c}>{c === "_all" ? "All categories" : c}</option>)}
                </select>
              </div>
            </div>
            <div className="rounded-xl border border-border max-h-72 overflow-y-auto divide-y divide-border">
              {filtered.map((t) => {
                const on = form.template_ids.has(t.template_id);
                return (
                  <label
                    key={t.template_id}
                    className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-muted/40 transition-colors ${on ? "bg-primary/5" : ""}`}
                    data-testid={`journey-type-template-row-${t.template_id}`}
                  >
                    <Checkbox
                      checked={on}
                      onCheckedChange={() => toggle(t.template_id)}
                      className="mt-1"
                      data-testid={`journey-type-template-toggle-${t.template_id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug break-words">{t.name}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug break-words">
                        <span className="uppercase tracking-widest font-mono">{t.category}</span>
                        {t.focus && <span> · {t.focus}</span>}
                      </p>
                    </div>
                  </label>
                );
              })}
              {filtered.length === 0 && (
                <p className="p-6 text-sm text-muted-foreground text-center">No matching programs.</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2 pt-2">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="journey-type-active-toggle" />
            Active
          </label>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" className="rounded-xl gap-2" onClick={onClose} data-testid="journey-type-cancel">
              <X className="h-4 w-4" /> Cancel
            </Button>
            <Button className="btn-brand rounded-xl gap-2" onClick={save} disabled={saving} data-testid="journey-type-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AdminJourneyTypesManager;
